import { ref } from 'vue';

/**
 * 核心分页逻辑 - 插件化架构的基础
 * @param {Object} options 配置选项
 * @param {number} options.defaultPage 默认页码
 * @param {number} options.defaultSize 默认页大小
 * @param {Function} options.bindChange 数据变化时的异步回调函数
 * @returns {Object} 分页相关的响应式数据和方法，支持插件扩展
 */
export const usePaginationCore = (options = {}) => {
	const { defaultPage = 1, defaultSize = 10, bindChange, debug = false } = options;

	const currentPage = ref(defaultPage);
	const pageSize = ref(defaultSize);
	const pageTotal = ref(0);

	// 插件系统
	const plugins = [];
	const pluginContext = {};

	const debugLog = (...args) => {
		if (debug) {
			console.log('[Pagination Core]', ...args);
		}
	};

	const runAsyncChange = async (context = {}) => {
		// 预留插件钩子：beforeChange
		for (const plugin of plugins) {
			if (plugin.beforeChange) {
				await plugin.beforeChange({ currentPage, pageSize, pageTotal, pluginContext, debugLog, ...context });
			}
		}

		let result;
		if (typeof bindChange === 'function') {
			result = await bindChange();
		}

		// 预留插件钩子：afterChange
		for (const plugin of plugins) {
			if (plugin.afterChange) {
				await plugin.afterChange({ currentPage, pageSize, pageTotal, pluginContext, debugLog, ...context });
			}
		}

		return result || Promise.resolve();
	};

	const setCurrentPage = val => {
		currentPage.value = val;
	};

	const changeCurrentPage = async val => {
		setCurrentPage(val);
		return await runAsyncChange({ type: 'page', newValue: val });
	};

	const changePageSize = async val => {
		pageSize.value = val;
		return await runAsyncChange({ type: 'size', newValue: val });
	};

	const changePageTotal = total => {
		pageTotal.value = total;
	};

	const refreshPagination = async () => {
		currentPage.value = defaultPage;
		pageSize.value = defaultSize;
		return await runAsyncChange({ type: 'refresh' });
	};

	// 插件系统方法
	const use = plugin => {
		if (typeof plugin === 'function') {
			plugin = plugin();
		}

		if (plugin && typeof plugin === 'object') {
			plugins.push(plugin);

			// 如果插件有 install 方法，执行安装
			if (plugin.install) {
				plugin.install({
					currentPage,
					pageSize,
					pageTotal,
					changeCurrentPage,
					setCurrentPage,
					changePageSize,
					changePageTotal,
					refreshPagination,
					runAsyncChange,
					pluginContext,
					debugLog
				});
			}
		}

		return paginationInstance;
	};

	const paginationInstance = {
		// 核心状态和方法
		currentPage,
		pageSize,
		pageTotal,
		changeCurrentPage,
		setCurrentPage,
		changePageSize,
		changePageTotal,
		refreshPagination,

		// 插件系统
		use,

		// 插件上下文（供插件扩展使用）
		pluginContext
	};

	return paginationInstance;
};
